import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CgSpinner } from 'react-icons/cg';
import { FaArrowLeft, FaCheck, FaChevronDown, FaChevronRight, FaKey, FaSyncAlt, FaTimes, FaTrash, FaUserPlus, FaUserShield } from 'react-icons/fa';
import { FaPeopleGroup } from 'react-icons/fa6';
import { toast } from 'react-toastify';

import { userAppData } from '../MainApp.js';
import Header from '../common/header.js';
import PolarSwitch from '../common/formComponents/polarSwitch.js';
import SysManagerAddStaff from './SysManagerAddStaff.js';
import StaffPermissionOverrides from './StaffPermissionOverrides.js';
import {
  disableSysManagerStaff,
  enableSysManagerStaff,
  getStaffSyncErrorDetails,
  getStaffSyncResults,
  getSysManagerStaff,
  getSysManagerStaffPermissionOverrides,
  getSysManagerStaffSystemAccess,
  resetSysManagerStaffPassword,
  updateSysManagerStaffPermissionOverride,
  updateSysManagerStaffSystemAccess,
} from './sysManagerStaffApi.js';

import './SysManagerStaff.css';

const TOAST_CONFIG = { autoClose: 4000, position: 'top-right' };
const SYSTEM_MANAGER_PERMISSIONS = [
  ['generalAdmin', 'General settings'],
  ['learnerAdmin', 'Learner administration'],
  ['staffAdmin', 'Staff administration'],
  ['bookingsAdmin', 'Bookings administration'],
  ['contactsAdmin', 'Contacts administration'],
  ['rotaAdmin', 'Rotas administration'],
  ['reproAdmin', 'Reprographics administration'],
  ['onCallAdmin', 'OnCall administration'],
  ['performanceManagementAdmin', 'Performance Management administration'],
  ['forms', 'Forms administration'],
  ['toolsAdmin', 'Tools administration'],
];
const STATUS_FILTERS = [
  {
    value: 'active',
    label: 'Active staff',
    description: 'Show staff accounts that are currently enabled.',
  },
  {
    value: 'disabled',
    label: 'Deactivated staff',
    description: 'Show staff accounts that have been disabled.',
  },
];
/**
 * Return a single initial for the staff avatar placeholder.
 * @param {string} displayName - Staff display name.
 * @returns {string} Uppercase initial or fallback.
 */
function getAvatarInitial(displayName) {
  const cleanName = String(displayName || '').trim();
  return cleanName ? cleanName.charAt(0).toUpperCase() : '?';
}

/**
 * Normalise one staff row so the UI can render safely across old/new payloads.
 * @param {object} rawStaffMember - Staff item from the API.
 * @returns {object} UI-safe staff row.
 */
function normaliseStaffMember(rawStaffMember) {
  return {
    cogId: String(rawStaffMember?.cogId || '').trim(),
    displayName: String(rawStaffMember?.displayName || '').trim(),
    email: String(rawStaffMember?.email || '').trim(),
    lastSeenIso: String(rawStaffMember?.lastSeenIso || '').trim(),
    profileImageUrl: String(rawStaffMember?.profileImageUrl || '').trim(),
    isDisabled: Number(rawStaffMember?.isDisabled ?? 0) === 1 ? 1 : 0,
  };
}

/**
 * Format the staff last-seen timestamp for the System Manager table.
 * @param {string} lastSeenIso - ISO timestamp from the permissions table.
 * @returns {{label: string, title: string, isEmpty: boolean}} Display metadata.
 */
function formatLastSeen(lastSeenIso) {
  const cleanValue = String(lastSeenIso || '').trim();
  if (!cleanValue) {
    return {
      label: 'Never seen',
      title: 'No login has been recorded for this account.',
      isEmpty: true,
    };
  }

  const parsedDate = new Date(cleanValue);
  if (Number.isNaN(parsedDate.getTime())) {
    return {
      label: 'Unknown',
      title: cleanValue,
      isEmpty: true,
    };
  }

  return {
    label: new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(parsedDate),
    title: parsedDate.toLocaleString('en-GB'),
    isEmpty: false,
  };
}

/** Display and administer staff accounts in System Manager. */
export default function SysManagerStaff() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [staffItems, setStaffItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [staffStatus, setStaffStatus] = useState('active');
  const [disableModalStaff, setDisableModalStaff] = useState(null);
  const [disablingCogId, setDisablingCogId] = useState('');
  const [enableModalStaff, setEnableModalStaff] = useState(null);
  const [enablingCogId, setEnablingCogId] = useState('');
  const [resetModalStaff, setResetModalStaff] = useState(null);
  const [resettingCogId, setResettingCogId] = useState('');
  const [permissionsModalStaff, setPermissionsModalStaff] = useState(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [permissionOverrides, setPermissionOverrides] = useState(null);
  const [updatingOverride, setUpdatingOverride] = useState('');
  const [systemAccess, setSystemAccess] = useState(null);
  const [updatingSystemAccess, setUpdatingSystemAccess] = useState('');
  const [systemAccessExpanded, setSystemAccessExpanded] = useState(false);
  const [addStaffOpen, setAddStaffOpen] = useState(false);
  const [syncResultsEnabled, setSyncResultsEnabled] = useState(false);
  const [syncResultsOpen, setSyncResultsOpen] = useState(false);
  const [syncResults, setSyncResults] = useState([]);
  const [syncResultsLoading, setSyncResultsLoading] = useState(false);
  const [syncResultsFailed, setSyncResultsFailed] = useState(false);
  const [syncErrorsOpen, setSyncErrorsOpen] = useState(false);
  const [syncErrors, setSyncErrors] = useState([]);
  const [syncErrorsLoading, setSyncErrorsLoading] = useState(false);
  const [syncErrorsFailed, setSyncErrorsFailed] = useState(false);
  const [selectedSyncRun, setSelectedSyncRun] = useState(null);

  const filteredStaff = useMemo(() => {
    const cleanSearchText = searchText.trim().toLowerCase();

    return staffItems.filter((staffMember) => {
      if (!cleanSearchText) {
        return true;
      }

      const searchableText = [
        staffMember.displayName,
        staffMember.email,
        staffMember.cogId,
        staffMember.lastSeenIso,
      ].join(' ').toLowerCase();

      return searchableText.includes(cleanSearchText);
    });
  }, [searchText, staffItems]);

  async function loadStaffList(nextStatus = staffStatus) {
    setLoading(true);
    setLoadFailed(false);

    try {
      const response = await getSysManagerStaff(nextStatus);
      const normalisedItems = response.items
        .map(normaliseStaffMember)
        .filter((staffMember) => staffMember.cogId && staffMember.displayName);
      setStaffItems(normalisedItems);
    } catch (error) {
      console.error('Failed to load System Manager staff list', error);
      setLoadFailed(true);
      setStaffItems([]);
      toast.error('❌ Failed to load staff accounts.', TOAST_CONFIG);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const sysManagerPermissions = userAppData?.sysManager || {};
    if (
      Number(sysManagerPermissions?.perms ?? 0) !== 1
      || sysManagerPermissions?.staffAdmin !== true
    ) {
      navigate('/app');
      return;
    }

    loadStaffList('active');

    let isCurrent = true;
    getStaffSyncResults(true)
      .then((response) => {
        if (isCurrent) setSyncResultsEnabled(response.enabled === true);
      })
      .catch((error) => {
        console.error('Failed to check Wonde staff sync availability', error);
        if (isCurrent) setSyncResultsEnabled(false);
      });
    return () => { isCurrent = false; };
  }, [navigate]);

  /** Open the staff results dialog and fetch the latest five staff runs. */
  async function openSyncResults() {
    setSyncResultsOpen(true);
    setSyncResultsLoading(true);
    setSyncResultsFailed(false);
    try {
      const response = await getStaffSyncResults(false);
      if (!response.enabled) {
        setSyncResultsEnabled(false);
        setSyncResultsOpen(false);
        return;
      }
      setSyncResults(response.items);
    } catch (error) {
      console.error('Failed to load Wonde staff sync results', error);
      setSyncResultsFailed(true);
      toast.error('❌ Failed to load staff sync results.', TOAST_CONFIG);
    } finally {
      setSyncResultsLoading(false);
    }
  }

  /** Load error records belonging to one staff-sync run. */
  async function openSyncErrors(result) {
    if (!result?.logId || Number(result.errors || 0) < 1) return;
    setSelectedSyncRun(result);
    setSyncErrorsOpen(true);
    setSyncErrorsLoading(true);
    setSyncErrorsFailed(false);
    setSyncErrors([]);
    try {
      const response = await getStaffSyncErrorDetails(result.logId);
      setSyncErrors(response.items);
    } catch (error) {
      console.error('Failed to load Wonde staff sync errors', error);
      setSyncErrorsFailed(true);
      toast.error('❌ Failed to load staff sync errors.', TOAST_CONFIG);
    } finally {
      setSyncErrorsLoading(false);
    }
  }

  /** Format an audit timestamp for the administrator's local timezone. */
  function formatSyncDate(value) {
    const parsedDate = new Date(value);
    return Number.isNaN(parsedDate.getTime()) ? 'Not available' : parsedDate.toLocaleString();
  }

  /** Convert stored audit action codes into administrator-friendly labels. */
  function formatSyncErrorAction(action) {
    const labels = {
      ERROR: 'Error',
      SKIPPED_DUPLICATE_EMAIL: 'Duplicate email',
      SKIPPED_MISSING_EMAIL: 'Missing email',
    };
    return labels[action] || String(action || 'Error').replaceAll('_', ' ');
  }

  function handleStatusChange(nextStatus) {
    if (nextStatus === staffStatus || loading) {
      return;
    }

    setStaffStatus(nextStatus);
    setSearchText('');
    loadStaffList(nextStatus);
  }

  async function handleDisableStaff() {
    const targetCogId = String(disableModalStaff?.cogId || '').trim();
    const displayName = String(disableModalStaff?.displayName || 'this user').trim();

    if (!targetCogId || disablingCogId) {
      return;
    }

    setDisablingCogId(targetCogId);
    try {
      await disableSysManagerStaff({ targetCogId });
      setStaffItems((currentItems) => currentItems.filter((staffMember) => (
        staffMember.cogId !== targetCogId
      )));
      setDisableModalStaff(null);
      toast.success(`✅ Disabled ${displayName}'s account.`, TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to disable staff account', error);
      toast.error(`❌ Failed to disable ${displayName}'s account.`, TOAST_CONFIG);
    } finally {
      setDisablingCogId('');
    }
  }

  async function handleEnableStaff() {
    const targetCogId = String(enableModalStaff?.cogId || '').trim();
    const displayName = String(enableModalStaff?.displayName || 'this user').trim();

    if (!targetCogId || enablingCogId) {
      return;
    }

    setEnablingCogId(targetCogId);
    try {
      await enableSysManagerStaff({ targetCogId });
      setStaffItems((currentItems) => currentItems.filter((staffMember) => (
        staffMember.cogId !== targetCogId
      )));
      setEnableModalStaff(null);
      toast.success(`✅ Enabled ${displayName}'s account.`, TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to enable staff account', error);
      toast.error(`❌ Failed to enable ${displayName}'s account.`, TOAST_CONFIG);
    } finally {
      setEnablingCogId('');
    }
  }

  async function handleResetPassword() {
    const targetCogId = String(resetModalStaff?.cogId || '').trim();
    const displayName = String(resetModalStaff?.displayName || 'this user').trim();

    if (!targetCogId || resettingCogId) {
      return;
    }

    setResettingCogId(targetCogId);
    try {
      await resetSysManagerStaffPassword({ targetCogId });
      setResetModalStaff(null);
      toast.success(`✅ Password reset email sent to ${displayName}.`, TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to send staff password reset email', error);
      toast.error(`❌ Failed to send password reset email to ${displayName}.`, TOAST_CONFIG);
    } finally {
      setResettingCogId('');
    }
  }

  /** Open the access modal and load the staff member's current section flags. */
  async function openPermissionsModal(staffMember) {
    setPermissionsModalStaff(staffMember);
    setPermissionOverrides(null);
    setSystemAccess(null);
    setSystemAccessExpanded(false);
    setPermissionsLoading(true);
    try {
      const [overridesResponse, systemAccessResponse] = await Promise.all([
        getSysManagerStaffPermissionOverrides(staffMember.cogId),
        getSysManagerStaffSystemAccess(staffMember.cogId),
      ]);
      setPermissionOverrides(overridesResponse);
      setSystemAccess(systemAccessResponse.systemAccess);
    } catch (error) {
      console.error('Failed to load staff permissions', error);
      toast.error('❌ Failed to load staff permissions.', TOAST_CONFIG);
    } finally {
      setPermissionsLoading(false);
    }
  }

  /** Save one direct System Manager permission for the selected staff user. */
  async function handleSystemAccessChange(permission, enabled) {
    const targetCogId = String(permissionsModalStaff?.cogId || '').trim();
    if (!targetCogId || updatingSystemAccess) return;
    setUpdatingSystemAccess(permission);
    try {
      const response = await updateSysManagerStaffSystemAccess({ targetCogId, permission, enabled });
      setSystemAccess(response.systemAccess);
      toast.success('✅ System Manager permission updated.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update System Manager permission', error);
      toast.error(`❌ ${error?.message || 'Failed to update System Manager permission.'}`, TOAST_CONFIG);
    } finally {
      setUpdatingSystemAccess('');
    }
  }

  /** Save one individual difference and refresh the effective permission view. */
  async function handlePermissionOverride(path, value) {
    const targetCogId = String(permissionsModalStaff?.cogId || '').trim();
    if (!targetCogId || updatingOverride) return;
    setUpdatingOverride(path);
    try {
      const response = await updateSysManagerStaffPermissionOverride({ targetCogId, path, value });
      setPermissionOverrides(response);
      toast.success('✅ Individual permission updated.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to update staff permission override', error);
      toast.error(`❌ ${error?.message || 'Failed to update individual permission.'}`, TOAST_CONFIG);
    } finally {
      setUpdatingOverride('');
    }
  }

  /** Remove every individual override so the user's group permissions apply unchanged. */
  async function handleRemoveAllPermissionOverrides() {
    const targetCogId = String(permissionsModalStaff?.cogId || '').trim();
    const displayName = String(permissionsModalStaff?.displayName || 'this user').trim();
    const hasOverrides = Object.keys(permissionOverrides?.permissionOverrides || {}).length > 0;
    if (!targetCogId || updatingOverride || !hasOverrides) return;
    if (!window.confirm(`Remove all individual permission overrides for ${displayName}?`)) return;

    setUpdatingOverride('removeAll');
    try {
      const response = await updateSysManagerStaffPermissionOverride({
        targetCogId,
        action: 'removeAll',
      });
      setPermissionOverrides(response);
      toast.success('✅ All individual permission overrides removed.', TOAST_CONFIG);
    } catch (error) {
      console.error('Failed to remove staff permission overrides', error);
      toast.error(`❌ ${error?.message || 'Failed to remove individual permission overrides.'}`, TOAST_CONFIG);
    } finally {
      setUpdatingOverride('');
    }
  }

  /** Add the newly created account to the active list and close the wizard. */
  function handleStaffCreated(rawStaffMember) {
    const staffMember = normaliseStaffMember(rawStaffMember);
    setStaffStatus('active');
    setSearchText('');
    setStaffItems((currentItems) => [...currentItems, staffMember].sort((left, right) => (
      left.displayName.localeCompare(right.displayName, 'en-GB', { sensitivity: 'base' })
    )));
    setAddStaffOpen(false);
    toast.success('✅ Staff account created and invitation sent.', TOAST_CONFIG);
  }

  return (
    <div className='main_div'>
      <div className='internal_main_div sysManager_staff_container'>
        <Header
          title='Staff'
          subtitle='View staff accounts in the school permissions table and disable active accounts when access should be removed.'
          rightContent={<div className='sysManager_staff_headerActions'>
            <button className='module-page-header__button' type='button' onClick={() => navigate('/app/sysmanager')}><FaArrowLeft aria-hidden='true' /> Back to System Manager</button>
          </div>}
        />

        <section className='sysManager_staff_groupsSurface' aria-label='Staff management actions'>
          <button
            className='sysManager_staff_groupsButton'
            type='button'
            onClick={() => navigate('/app/sysmanager/groups')}
          >
            <FaPeopleGroup aria-hidden='true' />
            Manage Groups
          </button>
          {syncResultsEnabled && (
            <button className='sysManager_staff_groupsButton' type='button' onClick={openSyncResults}>
              <FaSyncAlt aria-hidden='true' />
              Sync Results
            </button>
          )}
          <button className='sysManager_staff_groupsButton' type='button' onClick={() => setAddStaffOpen(true)}>
            <FaUserPlus aria-hidden='true' />
            Add staff
          </button>
        </section>

        <section className='sysManager_staff_accessSurface'>
          <div className='sysManager_staff_accessBody'>
            <aside className='sysManager_staff_filters'>
              <div className='sysManager_staff_filtersCard'>
                <h2>Staff list</h2>
                <div className='sysManager_staff_filterList'>
                  {STATUS_FILTERS.map((option) => (
                    <label
                      className={`sysManager_staff_filterOption${staffStatus === option.value ? ' sysManager_staff_filterOption--active' : ''}`}
                      key={option.value}
                    >
                      <span className='sysManager_staff_filterControl'>
                        <input
                          type='radio'
                          name='sysmanager-staff-status-filter'
                          value={option.value}
                          checked={staffStatus === option.value}
                          disabled={loading}
                          onChange={(event) => handleStatusChange(event.target.value)}
                        />
                        <strong>{option.label}</strong>
                      </span>
                      <small>{option.description}</small>
                    </label>
                  ))}
                </div>
              </div>
            </aside>

            <div className='sysManager_staff_accessMain'>
              <div className='sysManager_staff_toolbar'>
                <div className='sysManager_staff_resultSummary'>
                  <strong>{filteredStaff.length}</strong>
                  <span>{staffStatus === 'disabled' ? 'deactivated' : 'active'} staff shown</span>
                </div>
                <input
                  type='search'
                  className='sysManager_staff_search'
                  value={searchText}
                  placeholder='Search staff...'
                  aria-label='Search staff'
                  onChange={(event) => setSearchText(event.target.value)}
                />
              </div>

              {loading ? (
                <div className='sysManager_staff_loading'>
                  <CgSpinner className='sysManager_staff_spinner' />
                  <p>Loading staff accounts...</p>
                </div>
              ) : loadFailed ? (
                <div className='sysManager_staff_emptyState'>
                  <p>Staff accounts could not be loaded.</p>
                  <button type='button' onClick={() => loadStaffList(staffStatus)}>Try again</button>
                </div>
              ) : filteredStaff.length === 0 ? (
                <div className='sysManager_staff_emptyState'>
                  No staff matched the current search and list setting.
                </div>
              ) : (
                <div className='sysManager_staff_tableWrap'>
                  <table className='sysManager_staff_table'>
                    <thead>
                      <tr>
                        <th>Staff member</th>
                        <th>Email</th>
                        <th>Last login</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStaff.map((staffMember) => (
                        <tr key={staffMember.cogId}>
                          <td>
                            <div className='sysManager_staff_userCell'>
                              <div className='sysManager_staff_avatarWrap'>
                                <div className='sysManager_staff_avatarPlaceholder'>
                                  {getAvatarInitial(staffMember.displayName)}
                                </div>
                                {staffMember.profileImageUrl && (
                                  <img
                                    className='sysManager_staff_avatar'
                                    src={staffMember.profileImageUrl}
                                    alt=''
                                    onError={(event) => {
                                      event.currentTarget.style.opacity = '0';
                                    }}
                                  />
                                )}
                              </div>
                              <div className='sysManager_staff_userText'>
                                <strong>{staffMember.displayName}</strong>
                              </div>
                            </div>
                          </td>
                          <td>{staffMember.email || 'Not set'}</td>
                          <td>
                            {(() => {
                              const lastSeen = formatLastSeen(staffMember.lastSeenIso);
                              return (
                                <span
                                  className={`sysManager_staff_lastSeen${lastSeen.isEmpty ? ' sysManager_staff_lastSeen--empty' : ''}`}
                                  title={lastSeen.title}
                                >
                                  {lastSeen.label}
                                </span>
                              );
                            })()}
                          </td>
                          <td>
                            {staffMember.isDisabled ? (
                              <button
                                type='button'
                                className='sysManager_staff_enableButton'
                                aria-label={`Enable ${staffMember.displayName}`}
                                title={`Enable ${staffMember.displayName}`}
                                onClick={() => setEnableModalStaff(staffMember)}
                              >
                                <FaCheck aria-hidden='true' />
                              </button>
                            ) : (
                              <div className='sysManager_staff_actionGroup'>
                                <button
                                  type='button'
                                  className='sysManager_staff_permissionsButton'
                                  aria-label={`Manage permissions for ${staffMember.displayName}`}
                                  title={`Manage permissions for ${staffMember.displayName}`}
                                  onClick={() => openPermissionsModal(staffMember)}
                                >
                                  <FaUserShield aria-hidden='true' />
                                </button>
                                <button
                                  type='button'
                                  className='sysManager_staff_resetButton'
                                  aria-label={`Send password reset email to ${staffMember.displayName}`}
                                  title={`Send password reset email to ${staffMember.displayName}`}
                                  onClick={() => setResetModalStaff(staffMember)}
                                >
                                  <FaKey aria-hidden='true' />
                                </button>
                                <button
                                  type='button'
                                  className='sysManager_staff_disableButton'
                                  aria-label={`Disable ${staffMember.displayName}`}
                                  title={`Disable ${staffMember.displayName}`}
                                  onClick={() => setDisableModalStaff(staffMember)}
                                >
                                  <FaTimes aria-hidden='true' />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </section>

        {syncResultsOpen && (
          <div className='sysManager_staff_syncOverlay' role='presentation' onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSyncResultsOpen(false);
          }}>
            <section className='sysManager_staff_syncModal' role='dialog' aria-modal='true' aria-labelledby='staff-sync-results-title'>
              <button className='sysManager_staff_syncClose' type='button' aria-label='Close staff sync results' onClick={() => setSyncResultsOpen(false)}>×</button>
              <div className='sysManager_staff_syncHeader'>
                <h2 id='staff-sync-results-title'>Wonde staff sync results</h2>
                <p>The five most recent staff synchronisations.</p>
              </div>
              {syncResultsLoading && <div className='sysManager_staff_syncStatus'><CgSpinner className='sysManager_staff_spinner' /><p>Loading sync results...</p></div>}
              {!syncResultsLoading && syncResultsFailed && <div className='sysManager_staff_syncStatus'><p>Staff sync results could not be loaded.</p><button type='button' onClick={openSyncResults}>Try again</button></div>}
              {!syncResultsLoading && !syncResultsFailed && syncResults.length === 0 && <div className='sysManager_staff_syncStatus'><p>No staff syncs have been recorded yet.</p></div>}
              {!syncResultsLoading && !syncResultsFailed && syncResults.length > 0 && (
                <div className='sysManager_staff_syncTableWrap'>
                  <table className='sysManager_staff_syncTable'>
                    <thead><tr><th>Run</th><th>Status</th><th>Added</th><th>Disabled</th><th>Updated</th><th>Errors</th></tr></thead>
                    <tbody>{syncResults.map((result) => (
                      <tr key={result.logId}>
                        <td data-label='Run'>{formatSyncDate(result.startedAt)}</td><td data-label='Status'>{result.status}</td><td data-label='Added'>{result.added}</td><td data-label='Disabled'>{result.archived}</td><td data-label='Updated'>{result.updated}</td>
                        <td data-label='Errors'>{Number(result.errors || 0) > 0 ? <button className='sysManager_staff_syncErrorButton' type='button' onClick={() => openSyncErrors(result)} aria-label={`View ${result.errors} staff sync errors`}>{result.errors}</button> : 0}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}

        {syncErrorsOpen && (
          <div className='sysManager_staff_syncOverlay sysManager_staff_syncOverlay--errors' role='presentation' onMouseDown={(event) => {
            if (event.target === event.currentTarget) setSyncErrorsOpen(false);
          }}>
            <section className='sysManager_staff_syncModal sysManager_staff_syncErrorsModal' role='dialog' aria-modal='true' aria-labelledby='staff-sync-errors-title'>
              <button className='sysManager_staff_syncClose' type='button' aria-label='Close staff sync errors' onClick={() => setSyncErrorsOpen(false)}>×</button>
              <div className='sysManager_staff_syncHeader'><h2 id='staff-sync-errors-title'>Staff sync errors</h2><p>Staff run from {formatSyncDate(selectedSyncRun?.startedAt)}</p></div>
              {syncErrorsLoading && <div className='sysManager_staff_syncStatus'><CgSpinner className='sysManager_staff_spinner' /><p>Loading sync errors...</p></div>}
              {!syncErrorsLoading && syncErrorsFailed && <div className='sysManager_staff_syncStatus'><p>Staff sync errors could not be loaded.</p><button type='button' onClick={() => openSyncErrors(selectedSyncRun)}>Try again</button></div>}
              {!syncErrorsLoading && !syncErrorsFailed && syncErrors.length === 0 && <div className='sysManager_staff_syncStatus'><p>No stored error details were found for this run.</p></div>}
              {!syncErrorsLoading && !syncErrorsFailed && syncErrors.length > 0 && <div className='sysManager_staff_syncErrorList'>{syncErrors.map((error, index) => (
                <article className='sysManager_staff_syncErrorItem' key={`${error.action}-${error.wondeId || error.entityId}-${index}`}>
                  <div><strong>{formatSyncErrorAction(error.action)}</strong><span>{error.displayName || 'Unknown staff member'}</span></div>
                  {error.message && <p>{error.message}</p>}
                  <dl>
                    {error.email && <><dt>Email</dt><dd>{error.email}</dd></>}
                    {error.staffCode && <><dt>Staff code</dt><dd>{error.staffCode}</dd></>}
                    {(error.wondeId || error.entityId) && <><dt>Wonde ID</dt><dd>{error.wondeId || error.entityId}</dd></>}
                  </dl>
                </article>
              ))}</div>}
            </section>
          </div>
        )}

        {addStaffOpen && <SysManagerAddStaff onClose={() => setAddStaffOpen(false)} onCreated={handleStaffCreated} />}

        {disableModalStaff && (
          <div className='sysManager_staff_modalBackdrop' role='presentation'>
            <div
              className='sysManager_staff_modal'
              role='dialog'
              aria-modal='true'
              aria-labelledby='sysmanager-staff-disable-title'
            >
              <button
                type='button'
                className='sysManager_staff_modalClose'
                aria-label='Close disable account confirmation'
                disabled={Boolean(disablingCogId)}
                onClick={() => setDisableModalStaff(null)}
              >
                ×
              </button>
              <div className='sysManager_staff_modalIcon'>
                <FaTimes aria-hidden='true' />
              </div>
              <h2 id='sysmanager-staff-disable-title'>Disable staff account?</h2>
              <p>
                Are you sure you want to disable {disableModalStaff.displayName}'s account?
                This will immediately disable their access to School Engine and all associated data.
              </p>
              <div className='sysManager_staff_modalActions'>
                <button
                  type='button'
                  className='sysManager_staff_modalDanger'
                  disabled={Boolean(disablingCogId)}
                  onClick={handleDisableStaff}
                >
                  {disablingCogId ? (
                    <>
                      <CgSpinner className='sysManager_staff_buttonSpinner' />
                      Disabling
                    </>
                  ) : (
                    'Yes, disable account'
                  )}
                </button>
                <button
                  type='button'
                  className='sysManager_staff_modalSecondary'
                  disabled={Boolean(disablingCogId)}
                  onClick={() => setDisableModalStaff(null)}
                >
                  No, keep active
                </button>
              </div>
            </div>
          </div>
        )}

        {permissionsModalStaff && (
          <div className='sysManager_staff_modalBackdrop' role='presentation'>
            <div
              className='sysManager_staff_modal sysManager_staff_permissionsModal'
              role='dialog'
              aria-modal='true'
              aria-labelledby='sysmanager-staff-permissions-title'
            >
              <button
                type='button'
                className='sysManager_staff_modalClose'
                aria-label='Close staff permissions'
                disabled={Boolean(updatingOverride || updatingSystemAccess)}
                onClick={() => setPermissionsModalStaff(null)}
              >
                ×
              </button>
              <div className='sysManager_staff_permissionsHeading'>
                <div className='sysManager_staff_modalIcon sysManager_staff_modalIcon--permissions'>
                  <FaUserShield aria-hidden='true' />
                </div>
                <div>
                  <h2 id='sysmanager-staff-permissions-title'>Staff permissions</h2>
                  <p>{permissionsModalStaff.displayName}</p>
                </div>
              </div>
              <p className='sysManager_staff_permissionsHelp'>
                Changes save immediately. System Manager access applies directly to this user; highlighted settings below are individual overrides of group defaults.
              </p>
              {permissionsLoading ? (
                <div className='sysManager_staff_permissionsLoading'>
                  <CgSpinner className='sysManager_staff_spinner' />
                  Loading access...
                </div>
              ) : permissionOverrides && systemAccess ? (
                <div className='sysManager_staff_permissionsContent'>
                  <section className='sysManager_staff_permissionsSection'>
                    <div className='sysManager_staff_permissionsSectionHeading'>
                      <h3>Group and individual permissions</h3>
                      <button
                        className='sysManager_staff_removeOverridesButton'
                        type='button'
                        disabled={Boolean(updatingOverride) || Object.keys(permissionOverrides.permissionOverrides || {}).length === 0}
                        onClick={handleRemoveAllPermissionOverrides}
                      >
                        {updatingOverride === 'removeAll' ? <CgSpinner className='sysManager_staff_buttonSpinner' aria-hidden='true' /> : <FaTrash aria-hidden='true' />}
                        Remove all overrides
                      </button>
                    </div>
                    <StaffPermissionOverrides data={permissionOverrides} savingPath={updatingOverride} onChange={handlePermissionOverride} />
                  </section>
                  <section className='sysManager_staff_permissionsSection'>
                    <div className='sysManager_staff_systemAccessHeader'>
                      <h3>System Manager</h3>
                      <div className='sysManager_staff_systemAccessActions'>
                        <span>{SYSTEM_MANAGER_PERMISSIONS.filter(([permission]) => systemAccess[permission] === true).length} of {SYSTEM_MANAGER_PERMISSIONS.length} selected</span>
                        <button
                          type='button'
                          className='sysManager_staff_systemAccessToggle'
                          aria-label={`${systemAccessExpanded ? 'Collapse' : 'Expand'} System Manager permissions`}
                          aria-expanded={systemAccessExpanded}
                          onClick={() => setSystemAccessExpanded((currentValue) => !currentValue)}
                        >
                          {systemAccessExpanded ? <FaChevronDown aria-hidden='true' /> : <FaChevronRight aria-hidden='true' />}
                        </button>
                      </div>
                    </div>
                    {systemAccessExpanded && (
                      <div className='staff-overrides__options sysManager_staff_systemAccessOptions'>
                        {SYSTEM_MANAGER_PERMISSIONS.map(([permission, label]) => (
                          <div className='staff-overrides__row' key={permission}>
                            <span>{label}</span>
                            <PolarSwitch
                              ariaLabel={label}
                              data={systemAccess[permission] === true}
                              loading={updatingSystemAccess === permission}
                              disabled={Boolean(updatingSystemAccess || updatingOverride)}
                              setData={(enabled) => handleSystemAccessChange(permission, enabled)}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                </div>
              ) : (
                <div className='sysManager_staff_permissionsError'>Access settings could not be loaded.</div>
              )}
            </div>
          </div>
        )}

        {enableModalStaff && (
          <div className='sysManager_staff_modalBackdrop' role='presentation'>
            <div
              className='sysManager_staff_modal'
              role='dialog'
              aria-modal='true'
              aria-labelledby='sysmanager-staff-enable-title'
            >
              <button
                type='button'
                className='sysManager_staff_modalClose'
                aria-label='Close enable account confirmation'
                disabled={Boolean(enablingCogId)}
                onClick={() => setEnableModalStaff(null)}
              >
                ×
              </button>
              <div className='sysManager_staff_modalIcon sysManager_staff_modalIcon--enable'>
                <FaCheck aria-hidden='true' />
              </div>
              <h2 id='sysmanager-staff-enable-title'>Enable staff account?</h2>
              <p>
                Are you sure you want to enable {enableModalStaff.displayName}'s account?
                This will restore their account and allow access according to their saved permissions.
              </p>
              <div className='sysManager_staff_modalActions'>
                <button
                  type='button'
                  className='sysManager_staff_modalSuccess'
                  disabled={Boolean(enablingCogId)}
                  onClick={handleEnableStaff}
                >
                  {enablingCogId ? (
                    <>
                      <CgSpinner className='sysManager_staff_buttonSpinner' />
                      Enabling
                    </>
                  ) : (
                    'Yes, enable account'
                  )}
                </button>
                <button
                  type='button'
                  className='sysManager_staff_modalSecondary'
                  disabled={Boolean(enablingCogId)}
                  onClick={() => setEnableModalStaff(null)}
                >
                  No, keep disabled
                </button>
              </div>
            </div>
          </div>
        )}

        {resetModalStaff && (
          <div className='sysManager_staff_modalBackdrop' role='presentation'>
            <div
              className='sysManager_staff_modal'
              role='dialog'
              aria-modal='true'
              aria-labelledby='sysmanager-staff-reset-title'
            >
              <button
                type='button'
                className='sysManager_staff_modalClose'
                aria-label='Close password reset confirmation'
                disabled={Boolean(resettingCogId)}
                onClick={() => setResetModalStaff(null)}
              >
                ×
              </button>
              <div className='sysManager_staff_modalIcon sysManager_staff_modalIcon--reset'>
                <FaKey aria-hidden='true' />
              </div>
              <h2 id='sysmanager-staff-reset-title'>Send password reset?</h2>
              <p>
                Send a secure password reset email to {resetModalStaff.displayName}.
                They will choose their own new password through the standard reset flow.
              </p>
              <div className='sysManager_staff_modalActions'>
                <button
                  type='button'
                  className='sysManager_staff_modalPrimary'
                  disabled={Boolean(resettingCogId)}
                  onClick={handleResetPassword}
                >
                  {resettingCogId ? (
                    <>
                      <CgSpinner className='sysManager_staff_buttonSpinner' />
                      Sending
                    </>
                  ) : (
                    'Yes, send reset email'
                  )}
                </button>
                <button
                  type='button'
                  className='sysManager_staff_modalSecondary'
                  disabled={Boolean(resettingCogId)}
                  onClick={() => setResetModalStaff(null)}
                >
                  No, cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
